import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
    Box, Typography, Tabs, Tab, Button, TextField, Stack, Paper, Alert, Dialog, DialogTitle, DialogContent, IconButton,
    Table, TableBody, TableHead, TableRow, TableCell, DialogActions, Divider
} from '@mui/material'
import AppNav from '../../components/appNav';
import CircularProgress from '@mui/material/CircularProgress';
import TitleText from '../../components/TitleText'
import Person from '@mui/icons-material/Person'
import placeholderImage from '../../assets/placeholder-image.jpeg'
import CloseIcon from '@mui/icons-material/Close';
import { api, baseURL } from '../../services/api'
import { validateBr } from 'js-brasil';
import TablePayment from '../../components/TablePayment';
import { maskCNPJ, maskCPF2 } from '../../utils/mask';
import { isPast, parseISO, isWithinInterval, format, differenceInDays } from 'date-fns'
import TableInfo from '../../components/TableInfo';
import { Document, Page, pdfjs, } from 'react-pdf';
import "react-pdf/dist/Page/TextLayer.css";
import DownloadIcon from '@mui/icons-material/Download';
import Fab from '@mui/material/Fab';
import { ManagerContext } from '../../context/ManagerContext';
import PrintIcon from '@mui/icons-material/Print';

// Estilos do Quill para preservar indentação em conteúdo renderizado
const quillStyles = `
.quill-content {
    box-sizing: border-box;
    line-height: 1.42;
    white-space: pre-wrap;
    tab-size: 4;
    word-wrap: break-word;
}
.quill-content p,
.quill-content .ql-editor p {
    margin: 0;
    padding: 0;
}
.quill-content h1,
.quill-content h2,
.quill-content h3,
.quill-content h4,
.quill-content h5,
.quill-content h6 {
    margin: 0;
    padding: 0;
    font-weight: inherit;
    line-height: 1.42;
}
.quill-content .ql-indent-1 { padding-left: 3em; }
.quill-content .ql-indent-2 { padding-left: 6em; }
.quill-content .ql-indent-3 { padding-left: 9em; }
.quill-content .ql-indent-4 { padding-left: 12em; }
.quill-content .ql-indent-5 { padding-left: 15em; }
.quill-content .ql-indent-6 { padding-left: 18em; }
.quill-content .ql-indent-7 { padding-left: 21em; }
.quill-content .ql-indent-8 { padding-left: 24em; }
.quill-content blockquote {
    border-left: 4px solid #ccc;
    margin-bottom: 5px;
    margin-top: 5px;
    padding-left: 16px;
}
.quill-content ol,
.quill-content ul {
    margin: 0;
    padding-left: 1.5em;
}
.quill-content li.ql-indent-1 { padding-left: 3em; }
.quill-content li.ql-indent-2 { padding-left: 6em; }
.quill-content li.ql-indent-3 { padding-left: 9em; }
.quill-content li.ql-indent-4 { padding-left: 12em; }
.quill-content li.ql-indent-5 { padding-left: 15em; }
.quill-content li.ql-indent-6 { padding-left: 18em; }
.quill-content li.ql-indent-7 { padding-left: 21em; }
.quill-content li.ql-indent-8 { padding-left: 24em; }
`;

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3, width: "100%" }}>{children}</Box>
            )}
        </div>
    );
}

export default function EventDetails() {
    let { eventId } = useParams();
    let navigate = useNavigate()
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab') || '';
    const tshirtSize = queryParams.get('tshirtsize') || false;
    const groupsTo = queryParams.get('groups') || false;
    const hostingsTo = queryParams.get('hostings') || false;
    const currentBase = window.location.pathname.split('/')[1];
    const inscriptionTabLabel = currentBase === 'semper'
        ? 'Convite | Ingresso | Inscrição'
        : 'DAS INSCRIÇÕES';
    const tabLabels = [
        'Apresentação',
        inscriptionTabLabel,
        'Local',
        'Hospedagem',
        'Programação',
        '2ª Via de Boleto',
        'Palestrantes',
        ...(currentBase === 'apeprev' ? ['Recibo', 'Inscritos'] : []),
        '2ª Via do Certificado',
        'Materiais e Apresentações',
    ];
    const normalizeTabName = (value) => value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    const tabIndexByName = tabLabels.reduce((tabs, label, index) => {
        tabs[normalizeTabName(label)] = index;
        return tabs;
    }, {});
    const materialsTabIndex = tabIndexByName[normalizeTabName('Materiais e Apresentações')];
    let today = new Date();
    const { manager } = React.useContext(ManagerContext);
    const isAgip = manager?.config_path === 'agip';
    const [tshirt, setTshirt] = React.useState({
        open: tshirtSize === 'true',
        cpf: '',
        subscriber: null,
        value: '',
        loading: false,
        error: '',
        success: ''
    });
    const [groups, setGroups] = React.useState({
        open: groupsTo === 'true',
        cpf: '',
        subscriber: null,
        options: [],
        loading: false,
        error: '',
        success: ''
    });
    const [hostings, setHostings] = React.useState({
        open: hostingsTo === 'true',
        cpf: '',
        subscriber: null,
        options: [],
        loading: false,
        error: '',
        success: ''
    });
    const availableGroups = [
        { id: 'rpps_pr', name: 'RPPS PR - Destinados a todos que atuam em RPPS' },
        { id: 'rpps_invest', name: 'RPPS INVEST - Destinados aqueles que trabalham com investimentos' },
        { id: 'rpps_beneficios', name: 'RPPS BENEFICIOS - Destinados aqueles que trabalham com aposentadorias' },
        { id: 'rpps_contabil', name: 'RPPS CONTABIL - Destinados aos Contadores' },
        { id: 'rpps_juridico', name: 'RPPS JURIDICO - Destinados aos Advogados e Procuradores' },
        { id: 'rpps_presidentes', name: 'RPPS PRESIDENTES - Destinados aos Presidentes dos RPPS' },
        { id: 'rpps_comprev', name: 'RPPS COMPREV - Destinados aos que Trabalham com averbações, CTC, e Comprev' }
    ];
    const [inscriptions, setInscriptions] = React.useState([]);
    const [tabStep, setTabStep] = React.useState(() => {
        return tabIndexByName[normalizeTabName(tabParam)] ?? 0;
    })
    const [eventFiltered, setEventFiltered] = React.useState([])
    const [imageThumb, setImagethumb] = React.useState()
    const [subscriber, setSubscriber] = React.useState()
    const [externalId, setExternalId] = React.useState()
    const [company, setCompany] = React.useState()
    const [open, setOpen] = React.useState(false);
    const [openSubscribers, setOpenSubscribers] = React.useState(false);
    const [loadError, setLoadError] = React.useState()
    const [payments, setPayments] = React.useState([])
    const [genereteCertificate, setGenerateCertificate] = React.useState(false)
    const controlCertificate = isPast(parseISO(eventFiltered.date_finish))
    const controlSubscriber = useRef();
    const [openGuestDialog, setOpenGuestDialog] = React.useState(false);
    const [cpfGuest, setCpfGuest] = React.useState('');
    const [guestInfo, setGuestInfo] = React.useState(null);
    const [loadingGuest, setLoadingGuest] = React.useState(false);
    const [guestError, setGuestError] = React.useState('');
    const [linkMaps, setLinkMaps] = React.useState('');
    const [listFiles, setListFiles] = React.useState();
    const [openListFiles, setOpenListFiles] = React.useState(false);
    const [isPdf, setIsPdf] = useState({scheduling: false, partners: false});
    const [pdfWidth, setPdfWidth] = useState(1024);
    const [isPdfWorkerLoaded, setIsPdfWorkerLoaded] = useState(false);
    const [cpfBoleto, setCpfBoleto] = useState('');
    const [reciboDoc, setReciboDoc] = useState('');
    const [reciboResults, setReciboResults] = useState([]);
    const [reciboLoading, setReciboLoading] = useState(false);
    const [reciboError, setReciboError] = useState('');

    // Configuração do worker do PDF.js
    useEffect(() => {
        // Usa jsdelivr CDN que tem CORS habilitado
        pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        setIsPdfWorkerLoaded(true);
    }, []);

    useEffect(() => {
        console.log('location.search:', location.search);
        console.log('tshirtsize:', tshirtSize);
        console.log('groupsTo:', groupsTo);
        if (tshirtSize === 'true') {
            setTshirt(prev => ({ ...prev, open: true }));
        }
        if (groupsTo === 'true') {
            setGroups(prev => ({ ...prev, open: true }));
        }
        if (hostingsTo === 'true') {
            handleOpenGuestDialog();
        }
    }, [tshirtSize, groupsTo, hostingsTo]);

    async function getEventById(id) {
        try {
            const eventbyId = await api.get('/events/' + id + '?populate=*')
            console.log(eventbyId.data)
            if (!eventbyId.data.isActive && !eventbyId.data.isVisible) {
                setLoadError(true)
                return

            }

            setEventFiltered(...[eventbyId.data])
            let startDate = parseISO(eventbyId.data.start_subscription)
            let endDate = parseISO(eventbyId.data.end_subscription)
            controlSubscriber.current = !isWithinInterval(today, { start: startDate, end: endDate })
            setImagethumb({
                thumb: eventbyId.data?.thumbnail !== null && eventbyId.data.thumbnail?.formats?.small?.url ? baseURL + eventbyId.data.thumbnail.formats.small?.url : placeholderImage,
                width: eventbyId.data?.thumbnail !== null && eventbyId.data.thumbnail?.formats?.small?.width ? eventbyId.data.thumbnail.formats.small?.width : '600px',
                height: eventbyId.data?.thumbnail !== null && eventbyId.data.thumbnail?.formats?.small?.height ? eventbyId.data.thumbnail.formats.small?.height : '400px'
            })
            if (eventbyId.data.scheduling) {
                const url = (eventbyId.data.scheduling.startsWith('http') || eventbyId.data.scheduling.startsWith('https')) ? new URL(eventbyId.data.scheduling) : null;
                const isPdfUrl = url?.pathname.toLocaleLowerCase().endsWith('.pdf');
                console.log(url, isPdfUrl);
                setIsPdf(prev => ({ ...prev, scheduling: isPdfUrl }));
            }
            if (eventbyId.data.partners) {
                const url = (eventbyId.data.partners.startsWith('http') || eventbyId.data.partners.startsWith('https')) ? new URL(eventbyId.data.partners) : null;
                const isPdfUrl = url?.pathname.toLocaleLowerCase().endsWith('.pdf');
                console.log(url, isPdfUrl);
                setIsPdf(prev => ({ ...prev, partners: isPdfUrl }));

            }
        } catch (error) {
            setLoadError(true)
            console.log(error)
        }
    };

    const calculatePdfWidth = () => {
        const width = window.innerWidth;
        if (width < 600) { // mobile
            return width * 0.95; // 95% da largura da tela
        } else if (width < 960) { // tablet
            return width * 0.8;
        } else { // desktop
            return Math.min(1200, width * 0.9);
        }
    };

    // Adicione um useEffect para atualizar o tamanho quando a tela mudar
    useEffect(() => {
        const handleResize = () => {
            setPdfWidth(calculatePdfWidth());
        };

        handleResize(); // Calcula tamanho inicial
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        console.log(eventId)
        getEventById(eventId)
        getMaterials(eventId)
    }, [])



    async function getMaterials(eventId) {
        if (!eventId) return;
        try {
            const files = await api.get(`/materials?populate=*&filters[event]=${eventId}&filters[status]=true&pagination[page]=1&pagination[pageSize]=50`);
            if (files.status === 200) {
                const sortedFiles = [...files.data.data].sort((a, b) =>
                    (a.attributes?.title || '').localeCompare(b.attributes?.title || '', 'pt-BR', { sensitivity: 'base' })
                );
                setListFiles(sortedFiles);
            }
        } catch (error) {
            console.log('Error: ' + error)
        }

    };

    function gerarLinkGoogleMaps({ nome, cidade, estado }) {
        const query = `${nome}, ${cidade}, ${estado}`.replace(/\s/g, '+');
        return `https://www.google.com/maps/search/?api=1&query=${query}`;
    };

    const columns = [
        { Header: "#", accessor: "icon", width: "100%" },
        { Header: "Boleto", accessor: "external_id", width: "100%" },
        { Header: "Nome", accessor: "name", width: "100%" },
        { Header: "Baixar", accessor: "actions", width: "90%" },
    ];

    const columnsFiles = [
        { Header: "#", accessor: "icon", width: "100%" },
        { Header: "Arquivo", accessor: "title", width: "100%" },
        { Header: "Visualizar", accessor: "view", width: "90%" },
        { Header: "Baixar", accessor: "actions", width: "90%" },
    ];

    const handleView = ({ url }) => {
        window.open(url);
    };

    const handleClose = () => {
        setOpen(false);
    };

    async function handlePayment() {
        const isCNPJ = validateBr.cnpj(company)
        if (!isCNPJ) { return alert("CNPJ Inválido!") }
        try {
            const payment = await api.post('/order/payment/company', { data: { company, eventId } })
            if (payment.data.length == 0) return alert("Nehum boleto encontrado para este CNPJ")
            if (payment.data.length == 1) return window.open(payment.data[0].payment_url)
            payment.data.map(pay => {
                pay.name = pay.subscribers[0].name
            })
            setPayments(payment.data)
            setOpen(true);
        } catch (error) {
            console.log(error)
        }
    };

    async function handleBuscarBoletoIndividual() {
        const isCPF = validateBr.cpf(cpfBoleto);
        if (!isCPF) {
            return alert("CPF Inválido!");
        }
        
        try {
            const response = await api.get(`/order/individual/${cpfBoleto}`);
            
            if (response.data && response.data.payment_url) {
                window.open(response.data.payment_url, '_blank');
            } else {
                alert("Nenhum boleto encontrado para este CPF");
            }
        } catch (error) {
            console.error(error);

            alert(`${error.response?.data?.error?.message || 'Erro ao buscar boleto. Tente novamente.'}`);
        }
    };

    async function handleBuscarRecibo() {
        const digits = reciboDoc.replace(/\D/g, '');
        setReciboError('');
        setReciboResults([]);
        if (digits.length === 11) {
            if (!validateBr.cpf(reciboDoc)) return setReciboError('CPF inválido!');
            setReciboLoading(true);
            try {
                const { data } = await api.get(`/order/individual/${reciboDoc}/${eventId}`);
                if (data?.payment_url) {
                    let externalId = data.payment_url.split('/')[5];
                    let link = `/apeprev/recibo/${externalId}`;
                    window.open(link, '_blank');
                } else {
                    setReciboError('Nenhum boleto encontrado para este CPF.');
                }
            } catch (error) {
                setReciboError(error.response?.data?.error?.message || 'Erro ao buscar boleto.');
            }
            setReciboLoading(false);
        } else if (digits.length === 14) {
            if (!validateBr.cnpj(reciboDoc)) return setReciboError('CNPJ inválido!');
            setReciboLoading(true);
            try {
                const { data } = await api.get(`/order/company-subscribers/${reciboDoc.replace('/', '%2F')}/${eventId}`);
                if (!data || data.length === 0) {
                    setReciboError('Nenhuma inscrição encontrada para este CNPJ.');
                } else {
                    setReciboResults(data);
                }
            } catch (error) {
                setReciboError(error.response?.data?.error?.message || 'Erro ao buscar inscrições.');
            }
            setReciboLoading(false);
        } else {
            setReciboError('Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.');
        }
    };

    async function handleSubscribers() {
        const isCNPJ = validateBr.cnpj(company)
        if (!isCNPJ) { return alert("CNPJ Inválido!") }
        try {
            const subscribers = await api.get(`/order/company-subscribers/${company.replace('/', '%2F')}/${eventId}`)
            if (subscribers.data.length == 0) return alert("Nehum inscrito encontrado para este CNPJ")
            setInscriptions(subscribers.data)
            setOpenSubscribers(true);
        } catch (error) {
            console.log(error)
        }
    };

    async function handleMaterials() {
        setGenerateCertificate(true)
        try {
            const validSubscriber = await api.post('/order/tshirt/', { data: { cpf: subscriber, event: eventId } })
            if (validSubscriber.status == 200 && validSubscriber.data.id > 0) {
                setGenerateCertificate(false)
                setOpenListFiles(true)
                getMaterials(eventId)
            }
            if (validSubscriber.status == 404) {

            }
        } catch (error) {
            setGenerateCertificate(false)
            alert('Inscrição não Encontrada')
        }
    };

    async function handleCertificate() {
        setGenerateCertificate(true)
        try {
            const certificate = await api.post('/certificates', { data: { document: subscriber, event: eventFiltered.name } })
            if (certificate.status === 200) {
                window.open(`${baseURL + certificate.data[0].url}`, '_blank');
            }
            setGenerateCertificate(false)
        } catch (error) {
            console.log(error)
            setGenerateCertificate(false)
            alert(error.response.data.error.message)
        }
    };

    const handlePanel = (event, newStep) => {
        setTabStep(newStep);

        const nextTab = tabLabels[newStep];
        if (!nextTab) return;

        const nextQueryParams = new URLSearchParams(location.search);
        nextQueryParams.set('tab', nextTab);
        navigate(`${location.pathname}?${nextQueryParams.toString()}`, { replace: true });
    }

    const handleOpenGuestDialog = () => {
        setOpenGuestDialog(true);
        setGuestInfo(null);
        setGuestError('');
        setCpfGuest('');
        setLinkMaps('');
    };

    const handleCloseGuestDialog = () => {
        setOpenGuestDialog(false);
        setGuestInfo(null);
        setGuestError('');
        setCpfGuest('');
        setLinkMaps('');
    };

    const handleSearchGuest = async () => {
        setLoadingGuest(true);
        setGuestError('');
        setGuestInfo(null);

        try {
            const { data } = await api.post(`/order/hosting`, { data: { cpf: cpfGuest, event: eventId } });
            if (data && data.length > 0) {
                setGuestInfo(data[0]);
                setLinkMaps(gerarLinkGoogleMaps({ nome: data[0].hosting?.name || data[0].grid?.name, cidade: eventFiltered.city, estado: eventFiltered.state }))
            } else {
                setGuestError('SEM HOSPEDAGEM CONTEMPLADA.');
            }
        } catch (err) {
            setGuestError('SEM HOSPEDAGEM CONTEMPLADA');
        }
        setLoadingGuest(false);
    };

    const handleTshirtCpfChange = (e) => {
        setTshirt(prev => ({ ...prev, cpf: maskCPF2(e.target.value), error: '', success: '' }));
    };

    const handleTshirtSearch = async () => {
        setTshirt(prev => ({ ...prev, loading: true, error: '', subscriber: null, value: '', success: '' }));
        try {
            const { data } = await api.post(`/order/tshirt`, { data: { cpf: tshirt.cpf, event: eventId } });
            if (data && data?.name?.length > 0) {
                setTshirt(prev => ({
                    ...prev,
                    subscriber: data,
                    value: data.tshirtSize || '',
                    loading: false,
                    error: ''
                }));
            } else {
                setTshirt(prev => ({ ...prev, loading: false, error: 'Inscrito não encontrado para este CPF.' }));
            }
        } catch (err) {
            setTshirt(prev => ({ ...prev, loading: false, error: 'Erro ao buscar inscrito.' }));
        }
    };

    const handleTshirtSelect = (size) => {
        setTshirt(prev => ({ ...prev, value: size, error: '', success: '' }));
    };

    const handleTshirtSave = async () => {
        setTshirt(prev => ({ ...prev, loading: true, error: '', success: '' }));
        try {
            await api.put(`/order/tshirt/${tshirt.subscriber.id}`, {
                data: { tshirtSize: tshirt.value }
            });
            setTshirt(prev => ({
                ...prev,
                loading: false,
                success: 'Tamanho de camisa salvo com sucesso!'
            }));
        } catch (err) {
            setTshirt(prev => ({
                ...prev,
                loading: false,
                error: 'Erro ao salvar tamanho.'
            }));
        }
    };

    const handleTshirtClose = () => {
        setTshirt({
            open: false,
            cpf: '',
            subscriber: null,
            value: '',
            loading: false,
            error: '',
            success: ''
        });
    };
    // Funções para manipular os grupos
    const handleGroupsSelect = (groupId) => {
        setGroups(prev => {
            const newOptions = prev.options.includes(groupId)
                ? prev.options.filter(id => id !== groupId)
                : [...prev.options, groupId];
            return { ...prev, options: newOptions };
        });
    };

    const handleGroupsSave = async () => {
        setGroups(prev => ({ ...prev, loading: true, error: '', success: '' }));
        try {
            await api.put(`/order/tshirt/${groups.subscriber.id}`, {
                data: { groups: groups.options }
            });
            setGroups(prev => ({
                ...prev,
                loading: false,
                success: 'Grupos salvos com sucesso!'
            }));
        } catch (err) {
            setGroups(prev => ({
                ...prev,
                loading: false,
                error: 'Erro ao salvar grupos.'
            }));
        }
    };
    // Funções para controle dos grupos de WhatsApp
    const handleGroupsCpfChange = (e) => {
        setGroups(prev => ({
            ...prev,
            cpf: maskCPF2(e.target.value),
            error: '',
            success: ''
        }));
    };

    const handleGroupsSearch = async () => {
        setGroups(prev => ({
            ...prev,
            loading: true,
            error: '',
            subscriber: null,
            options: [],
            success: ''
        }));

        try {
            const { data } = await api.post(`/order/tshirt`, { data: { cpf: groups.cpf, event: eventId } });

            if (data && data?.name?.length > 0) {
                setGroups(prev => ({
                    ...prev,
                    subscriber: data,
                    options: data.groups || [],
                    loading: false,
                    error: ''
                }));
            } else {
                setGroups(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Inscrito não encontrado para este CPF.'
                }));
            }
        } catch (err) {
            if (err.response?.status == 404) {
                setGroups(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Inscrito não encontrado neste evento.\n Inscreva-se para selecionar os grupos!'
                }));
            } else {
                setGroups(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Erro ao buscar inscrito.'
                }));
            }
        }
    };

    const handleGroupsClose = () => {
        setGroups({
            open: false,
            cpf: '',
            subscriber: null,
            options: [],
            loading: false,
            error: '',
            success: ''
        });
    };

    const handleFilesClose = () => {
        setOpenListFiles(false);
    };

    function getMaterialUrl(id) {
        const file = listFiles?.find(file => id == file.id);
        if (!file) return null;
        if (file.attributes?.link) return file.attributes.link;
        const fileUrl = file.attributes?.file?.data?.[0]?.attributes?.url;
        return fileUrl ? `${baseURL + fileUrl}` : null;
    }

    function triggerDownload(url) {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function handleDownloadMaterial({ id }) {
        triggerDownload(getMaterialUrl(id));
    }

    const handlePrintReceipt = () => {
        window.print();
    };

    function handleViewMaterial({ id }) {
        const url = getMaterialUrl(id);
        if (url) window.open(url, '_blank');
    };

    const handleDownloadPdf = (url) => {
        window.open(url, '_blank');
    };

    // Modifique o componente PDFViewer para melhor controle de estado e renderização
    const PDFViewer = ({ pdfUrl }) => {
        const [localNumPages, setLocalNumPages] = useState(null);
        const [localPageNumber, setLocalPageNumber] = useState(1);
        const [localIsLoading, setLocalIsLoading] = useState(true);
        const [localError, setLocalError] = useState(false);
        const isMobile = window.innerWidth < 960;
        // Memorize o objeto file
        const pdfFile = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);

        useEffect(() => {
            setLocalPageNumber(1);
            setLocalNumPages(null);
            setLocalIsLoading(true);
            setLocalError(false);
        }, [tabStep]);

        if (isMobile) {
            return (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    my: 4
                }}>
                    <Alert severity="info">
                        Para melhor visualização em dispositivos móveis, faça o download do arquivo.
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => handleDownloadPdf(pdfUrl)}
                        startIcon={<DownloadIcon />}
                    >
                        Baixar PDF
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{
                width: '100%',
                minHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative'
            }}>
                {!isPdfWorkerLoaded ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        my: 4
                    }}>
                        <Alert severity="warning">
                            Visualizador de PDF não disponível. Por favor, faça o download do arquivo.
                        </Alert>
                        <Button
                            variant="contained"
                            onClick={() => handleDownloadPdf(pdfUrl)}
                            startIcon={<DownloadIcon />}
                        >
                            Baixar PDF
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'auto'
                        }}>
                            <Document
                                file={pdfFile}
                                onLoadSuccess={({ numPages }) => {
                                    setLocalNumPages(numPages);
                                    setLocalIsLoading(false);
                                    setLocalError(false);
                                }}
                                onLoadError={(error) => {
                                    console.error('Erro ao carregar PDF:', error);
                                    setLocalError(true);
                                    setLocalIsLoading(false);
                                }}
                                loading={
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        my: 4
                                    }}>
                                        <CircularProgress />
                                        <Typography>Carregando PDF...</Typography>
                                    </Box>
                                }
                                scale={0.85}
                                error={
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        my: 4
                                    }}>
                                        <Alert severity="error">
                                            Não foi possível carregar o PDF. Por favor, tente baixar o arquivo.
                                        </Alert>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleDownloadPdf(pdfUrl)}
                                            startIcon={<DownloadIcon />}
                                        >
                                            Baixar PDF
                                        </Button>
                                    </Box>
                                }
                            >
                                <Page
                                    key={`page_${localPageNumber}`}
                                    pageNumber={localPageNumber}
                                    width={pdfWidth}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    loading={
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minHeight: '50vh'
                                        }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    }
                                />
                            </Document>
                        </Box>

                        <Fab
                            color="primary"
                            sx={{
                                position: 'fixed',
                                bottom: 66,
                                right: 16,
                                zIndex: 1000
                            }}
                            onClick={() => handleDownloadPdf(pdfUrl)}
                        >
                            <DownloadIcon />
                        </Fab>

                        {localNumPages > 1 && !localError && !localIsLoading && (
                            <Box sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 2,
                                bgcolor: 'background.paper',
                                borderTop: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2,
                                zIndex: 999
                            }}>
                                <Button
                                    size="small"
                                    disabled={localPageNumber <= 1}
                                    onClick={() => setLocalPageNumber(prev => prev - 1)}
                                >
                                    Anterior
                                </Button>
                                <Typography variant="body2">
                                    {localPageNumber} / {localNumPages}
                                </Typography>
                                <Button
                                    size="small"
                                    disabled={localPageNumber >= localNumPages}
                                    onClick={() => setLocalPageNumber(prev => prev + 1)}
                                >
                                    Próxima
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ width: "100vw", height: "100vh", px: 0, m: 0, overflowX: 'hidden' }}>
            <style>{quillStyles}</style>
            <AppNav />
            <Dialog open={tshirt.open} onClose={handleTshirtClose} maxWidth="xs" fullWidth>
                <DialogTitle>Selecionar Tamanho da Camisa</DialogTitle>
                <DialogContent>
                    {!tshirt.subscriber ? (
                        <>
                            <TextField
                                label="CPF"
                                fullWidth
                                margin="normal"
                                value={tshirt.cpf}
                                onChange={handleTshirtCpfChange}
                                inputProps={{ maxLength: 14 }}
                                helperText={'Digite seu CPF para buscar sua inscrição'}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleTshirtSearch}
                                disabled={tshirt.loading || tshirt.cpf.replace(/\D/g, '').length !== 11}
                                sx={{ mb: 2 }}
                            >
                                {tshirt.loading ? 'Buscando...' : 'Avançar'}
                            </Button>
                            {tshirt.error && <Alert severity="error">{tshirt.error}</Alert>}
                        </>
                    ) : (
                        <>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                <b>Nome:</b> {tshirt.subscriber.name}
                            </Typography>
                            {tshirt.subscriber.tshirtSize ? (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    O tamanho da camisa já foi selecionado: <b>{tshirt.subscriber.tshirtSize}</b>
                                </Alert>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>Selecione o tamanho da camisa:</Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        {['P', 'M', 'G', 'GG', 'EG'].map(size => (
                                            <Button
                                                key={size}
                                                variant={tshirt.value === size ? 'contained' : 'outlined'}
                                                onClick={() => handleTshirtSelect(size)}
                                            >
                                                {size}
                                            </Button>
                                        ))}
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        fullWidth
                                        disabled={!tshirt.value || tshirt.loading}
                                        onClick={handleTshirtSave}
                                    >
                                        {tshirt.loading ? 'Salvando...' : 'Salvar'}
                                    </Button>
                                </>
                            )}
                            {tshirt.success && <Alert severity="success" sx={{ mt: 2 }}>{tshirt.success}</Alert>}
                            {tshirt.error && <Alert severity="error" sx={{ mt: 2 }}>{tshirt.error}</Alert>}
                        </>
                    )}
                </DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Button onClick={handleTshirtClose} color="primary">Fechar</Button>
                </Box>
            </Dialog>
            <Dialog open={groups.open} onClose={handleGroupsClose} maxWidth="sm" fullWidth>
                <DialogTitle>SOMENTE PARA INSCRITOS:</DialogTitle>
                <Typography mx={2}>Validar CPF para verificar inscrição:</Typography>
                <DialogContent>
                    {!groups.subscriber ? (
                        <>
                            <TextField
                                label="CPF"
                                fullWidth
                                margin="normal"
                                value={groups.cpf}
                                onChange={handleGroupsCpfChange}
                                inputProps={{ maxLength: 14 }}
                                helperText={'Digite seu CPF para buscar sua inscrição'}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleGroupsSearch}
                                disabled={groups.loading || groups.cpf.replace(/\D/g, '').length !== 11}
                                sx={{ mb: 2 }}
                            >
                                {groups.loading ? 'Buscando...' : 'Avançar'}
                            </Button>
                            {groups.error && <Alert severity="error">{groups.error}</Alert>}
                        </>
                    ) : (
                        <>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                <b>Nome:</b> {groups.subscriber.name}
                            </Typography>

                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Selecione os grupos que deseja participar:
                            </Typography>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                mb: 3
                            }}>
                                {availableGroups.map(group => (
                                    <Button
                                        key={group.id}
                                        variant={groups.options.includes(group.id) ? 'contained' : 'outlined'}
                                        onClick={() => handleGroupsSelect(group.id)}
                                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                    >
                                        {group.name}
                                    </Button>
                                ))}
                            </Box>

                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                disabled={groups.options.length === 0 || groups.loading}
                                onClick={handleGroupsSave}
                            >
                                {groups.loading ? 'Salvando...' : 'Salvar Seleção'}
                            </Button>

                            {groups.success && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    {groups.success}
                                </Alert>
                            )}
                            {groups.error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {groups.error}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleGroupsClose}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openListFiles} onClose={handleFilesClose} maxWidth="lg" fullWidth>
                <DialogTitle>Lista de Materiais/Apresentação</DialogTitle>
                <DialogContent>
                    {listFiles && listFiles.length > 0 &&
                        <TableInfo
                            title='Arquivos'
                            variant="secondary"
                            view={handleViewMaterial}
                            onView={handleViewMaterial}
                            onDownload={handleDownloadMaterial}
                            rows={listFiles}
                            columns={columnsFiles}
                        />
                    }
                </DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Button onClick={handleFilesClose} color="primary">Fechar</Button>
                </Box>
            </Dialog>
            <Dialog
                maxWidth={'md'}
                fullWidth
                open={openSubscribers}
                onClose={() => { setOpenSubscribers(false) }}
            >
                {inscriptions.length > 0 && (
                    <DialogContent elevation={2} sx={{ padding: 4 }}>
                        <style>
                            {`
                                @media print {
                                    body * {
                                        visibility: hidden;
                                    }
                                    #printable-receipt, #printable-receipt * {
                                        visibility: visible;
                                    }
                                    #printable-receipt {
                                        position: absolute;
                                        left: 0;
                                        top: 0;
                                        width: 100%;
                                    }
                                    .no-print {
                                        display: none !important;
                                    }
                                }
                            `}
                        </style>
                        <Box id="printable-receipt">
                            {/* Cabeçalho com logo */}
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                mb: 4,
                                pb: 3,
                                borderBottom: '2px solid #1D385E'
                            }}>
                                {manager.logo_url && (
                                    <img 
                                        src={manager.logo_url} 
                                        alt="Logo" 
                                        style={{ 
                                            maxWidth: '180px', 
                                            maxHeight: '100px',
                                            marginBottom: '16px',
                                            objectFit: 'contain'
                                        }} 
                                    />
                                )}
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        color: '#1D385E', 
                                        fontWeight: 700,
                                        textAlign: 'center',
                                        mb: 1
                                    }}
                                >
                                    COMPROVANTE DE INSCRIÇÃO
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        color: '#555', 
                                        textAlign: 'center',
                                        fontWeight: 500
                                    }}
                                >
                                    {eventFiltered.name}
                                </Typography>
                            </Box>

                            {/* Informações do evento */}
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 2, 
                                    mb: 3, 
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 2
                                }}
                            >
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Local:</strong> {eventFiltered.location_address || 'A definir'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Data:</strong> {eventFiltered.date_initial ? format(parseISO(eventFiltered.date_initial), 'dd/MM/yyyy') : 'A definir'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Data de emissão:</strong> {format(new Date(), 'dd/MM/yyyy HH:mm')}
                                </Typography>
                            </Paper>

                            {/* Lista de inscritos */}
                            <Box sx={{ mb: 3 }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mb: 2, 
                                        color: '#1D385E',
                                        fontWeight: 600
                                    }}
                                >
                                    Inscritos ({inscriptions.length})
                                </Typography>
                                <Table size="medium">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#1D385E' }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>#</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nome do Inscrito</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inscriptions.map((insc, idx) => (
                                            <TableRow 
                                                key={idx}
                                                sx={{ 
                                                    '&:nth-of-type(odd)': { 
                                                        backgroundColor: '#f9f9f9' 
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Person sx={{ color: '#1D385E', mr: 1 }} />
                                                        {idx + 1}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {String(insc.subscriber_name).toUpperCase()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>

                            {/* Rodapé */}
                            <Divider sx={{ my: 3 }} />
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Este é um comprovante de inscrição gerado automaticamente.
                                </Typography>
                                {manager.email && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Para mais informações: {manager.email}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Botões de ação */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }} className="no-print">
                            <Button 
                                variant="contained" 
                                startIcon={<PrintIcon />}
                                onClick={handlePrintReceipt}
                                sx={{ 
                                    backgroundColor: '#1D385E',
                                    '&:hover': {
                                        backgroundColor: '#2d4a6e'
                                    }
                                }}
                            >
                                Imprimir
                            </Button>
                            <Button 
                                variant="outlined" 
                                onClick={() => setOpenSubscribers(false)}
                            >
                                Fechar
                            </Button>
                        </Box>
                    </DialogContent>
                )}
            </Dialog>

            <Dialog
                onClose={handleClose}
                fullWidth
                maxWidth={'lg'}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    Boletos Aguardando Pagamento
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <TablePayment title='Boletos' variant="info" view={handleView} rows={payments} columns={columns} />
                </DialogContent>
            </Dialog>
            {
                eventFiltered.length === 0 ? (
                    <Stack
                        sx={{ display: 'flex', width: '100%', height: '90%', justifyContent: 'center' }}
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}>
                        {loadError ? <Alert sx={{ width: '50%' }} severity="error">Evento desativado ou erro ao carregar evento! Tente novamente mais tarde...</Alert> : <CircularProgress />}
                    </Stack>
                )
                    :
                    <>
                        <Paper elevation={3} sx={{ mx: 'auto', my: 1 }} elevated={2} style={{ width: imageThumb.width || 0, height: imageThumb.height || 0 }}>
                            <Box style={{ background: `url(${imageThumb.thumb || null}`, resizeMode: 'contain', backgroundRepeat: 'no-repeat', height: '100%', width: '100%' }}>
                                {eventFiltered.sold_out ?
                                    <Box style={{ display: 'flex', backgroundColor: "rgba(0,0,0,0.6)", width: '100%', justifyContent: 'center', textAlign: 'center', height: "100%", borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                                        <Typography color={'#FF8787'} sx={{ alignSelf: 'center' }} variant={'h3'}>ESGOTADO</Typography>
                                    </Box>
                                    : null}
                            </Box>
                        </Paper>
                        <Box sx={{ display: 'flex', width: '100%', bgcolor: 'background.paper', justifyContent: 'center' }} >
                            <Tabs value={tabStep} onChange={handlePanel} arial-label="tab panel event" variant="scrollable" scrollButtons={true}>
                                <Tab label="Apresentação" id="simple-tab-0" aria-controls="simple-tabpanel-0" />
                                <Tab label={ (currentBase == 'semper' ? 'Convite | Ingresso | Inscrição' : 'DAS INSCRIÇÕES')} id="simple-tab-1" aria-controls="simple-tabpanel-1" />
                                <Tab label="Local" id="simple-tab-2" aria-controls="simple-tabpanel-2" />
                                <Tab label="Hospedagem" id="simple-tab-3" aria-controls="simple-tabpanel-3" />
                                <Tab label="Programação" id="simple-tab-4" aria-controls="simple-tabpanel-4" />
                                <Tab label="2ª Via de Boleto" id="simple-tab-5" aria-controls="simple-tabpanel-5" />
                                <Tab label="Palestrantes" id="simple-tab-6" aria-controls="simple-tabpanel-6" />
                                {currentBase == 'apeprev' ? <Tab label="Recibo" id="simple-tab-7" aria-controls="simple-tabpanel-7" /> : null}
                                {currentBase == 'apeprev' ? <Tab label="Inscritos" id="simple-tab-8" aria-controls="simple-tabpanel-8" /> : null}
                                <Tab disabled={!controlCertificate} label="2ª Via Certificado" id="simple-tab-9" aria-controls="simple-tabpanel-9" />
                                <Tab disabled={listFiles ? listFiles.length < 1 : true} label="Materiais e Apresentações" id={`simple-tab-${materialsTabIndex}`} aria-controls={`simple-tabpanel-${materialsTabIndex}`} />


                            </Tabs>
                        </Box>
                        <Box sx={{ width: '100%', mb: 5, py: 4 }}>
                            <TabPanel value={tabStep} index={0}>
                                <TitleText>O EVENTO</TitleText>
                                <Typography
                                    variant="body1"
                                    sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }}
                                    textAlign="justify"
                                    component="div"
                                    className="quill-content"
                                    dangerouslySetInnerHTML={{ __html: eventFiltered.apresentation }}
                                />
                            </TabPanel>
                            <TabPanel value={tabStep} index={1}>
                                <TitleText>{(currentBase == 'semper' ? 'Convite | Ingresso | Inscrição' : 'DAS INSCRIÇÕES')} </TitleText>
                                <Button disabled={controlSubscriber.current || eventFiltered.sold_out} variant={"contained"} sx={{ display: "block", mt: 5, mx: "auto", borderRadius: 5, textAlign: 'center', maxWidth: { xs: '60%', sm: '40%' } }}
                                    onClick={() => {
                                        const route = eventFiltered.typeForm === 'subscriber'
                                            ? `/individual-subscriber/${eventId}`
                                            : eventFiltered.typeForm === 'auto'
                                                ? `/auto-subscribe/${eventId}`
                                                : `/subscribe/${eventId}`;
                                        navigate(route);
                                    }}>
                                    <Typography variant='h6'>{'Inscreva-se'}</Typography>
                                </Button>
                                <Typography variant={"body1"} sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }} textAlign="justify">
                                    <div className="quill-content" dangerouslySetInnerHTML={{ __html: eventFiltered.terms }}></div>
                                </Typography>

                            </TabPanel>
                            <TabPanel value={tabStep} index={2}>
                                <TitleText>DO LOCAL</TitleText>
                                <Typography variant={"h5"} sx={{ width: "80%", my: 3, mx: "auto", fontWeight: 500, color: '#1D385E' }} textAlign="justify">
                                    Local: {eventFiltered.location_address}
                                </Typography>
                                <Typography variant={"body1"} sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }} textAlign="justify">
                                    <div className="quill-content" dangerouslySetInnerHTML={{ __html: eventFiltered.location }}></div>
                                </Typography>
                            </TabPanel>
                            <TabPanel value={tabStep} index={3}>
                                <TitleText>DA HOSPEDAGEM</TitleText>
                                {(eventFiltered.hostings?.length > 0 || eventFiltered.hostingRelateds?.length > 0) && <Button
                                    variant="contained"
                                    sx={{ mt: 8, mx: '40%', width: '20%' }}
                                    onClick={handleOpenGuestDialog}
                                >
                                    Verificar minha hospedagem
                                </Button>
                                }
                                <Typography variant={"body1"} sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }} textAlign="justify" >
                                    <div className="quill-content" dangerouslySetInnerHTML={{ __html: eventFiltered.information_accommodation }}></div>
                                </Typography>

                            </TabPanel>
                            <TabPanel value={tabStep} index={4}>
                                <TitleText>PROGRAMAÇÃO</TitleText>
                                <Typography variant={"body1"} sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }} textAlign="justify">
                                    {!isPdf.scheduling && <div className="quill-content" dangerouslySetInnerHTML={{ __html: eventFiltered.scheduling }}></div>}
                                </Typography>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', position: 'relative' }}>
                                    {isPdf.scheduling && <PDFViewer pdfUrl={eventFiltered.scheduling} />}
                                </Box>
                            </TabPanel>
                            <TabPanel value={tabStep} index={5}>
                                <TitleText>2ª VIA DE BOLETO</TitleText>
                                {eventFiltered.typeForm === 'subscriber' ? (
                                    <>
                                        <TextField fullWidth sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                            id="cpf-boleto" label="CPF" variant="outlined" name="cpf"
                                            value={cpfBoleto}
                                            onChange={(e) => setCpfBoleto(maskCPF2(e.currentTarget.value))}
                                            inputProps={{ maxLength: 14 }}
                                        />
                                        <Button variant={"contained"} sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }} onClick={handleBuscarBoletoIndividual}>
                                            <Typography variant='h6'>BUSCAR</Typography>
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <TextField fullWidth sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                            id="CNPJ" label="CNPJ" variant="outlined" name="CNPJ"
                                            value={company}
                                            onChange={(e) => setCompany(maskCNPJ(e.currentTarget.value))}
                                        />
                                        <Button variant={"contained"} sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }} onClick={handlePayment}>
                                            <Typography variant='h6'>BUSCAR</Typography>
                                        </Button>
                                    </>
                                )}
                            </TabPanel>
                            <TabPanel value={tabStep} index={6}>
                                <TitleText>Palestrantes</TitleText>
                                <Typography variant={"body1"} sx={{ width: "80%", mt: 3, mb: 5, mx: "auto", whiteSpace: 'pre-wrap' }} textAlign="justify">
                                    {!isPdf.partners && <div className="quill-content" dangerouslySetInnerHTML={{ __html: eventFiltered.partners }}></div>}
                                </Typography>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', position: 'relative' }}>
                                    {isPdf.partners && <PDFViewer pdfUrl={eventFiltered.partners} />}
                                </Box>
                            </TabPanel>
                            {currentBase == 'apeprev' ?
                                <TabPanel value={tabStep} index={7}>
                                    <TitleText>RECIBO</TitleText>
                                    <TextField fullWidth
                                        sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                        inputProps={{ maxLength: 18 }}
                                        value={reciboDoc}
                                        id="reciboDoc"
                                        label="CPF ou CNPJ"
                                        variant="outlined"
                                        name="reciboDoc"
                                        helperText="Digite CPF para buscar diretamente ou CNPJ para listar inscritos"
                                        onChange={(e) => {
                                            const digits = e.currentTarget.value.replace(/\D/g, '');
                                            setReciboDoc(digits.length <= 11 ? maskCPF2(e.currentTarget.value) : maskCNPJ(e.currentTarget.value));
                                            setReciboError('');
                                            setReciboResults([]);
                                        }} />
                                    <Button
                                        onClick={handleBuscarRecibo}
                                        disabled={reciboLoading}
                                        variant={"contained"}
                                        sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }}>
                                        <Typography variant='h6'>{reciboLoading ? 'Buscando...' : 'Buscar Recibo'}</Typography>
                                    </Button>
                                    {reciboError && <Alert severity="error" sx={{ width: '60%', mx: 'auto', mt: 2 }}>{reciboError}</Alert>}
                                    {reciboResults.length > 0 && (
                                        <Box sx={{ width: '80%', mx: 'auto', mt: 3 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><b>Nome</b></TableCell>
                                                        <TableCell><b>Número do Boleto</b></TableCell>
                                                        <TableCell><b>Recibo</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {reciboResults.map((item, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{String(item.subscriber_name).toUpperCase()}</TableCell>
                                                            <TableCell>{item.external_id || 'Recibo indisponível'}</TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    href={`/apeprev/recibo/${item.external_id}`}
                                                                    target="_blank"
                                                                    disabled={!item.external_id}
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    Ver Recibo
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    )}
                                </TabPanel> :
                                <TabPanel value={tabStep} index={7}>
                                    <TitleText>2ª VIA DO CERTIFICADO</TitleText>
                                    <TextField fullWidth
                                        sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                        inputProps={{ maxLength: 14 }}
                                        value={subscriber}
                                        id="cpf"
                                        label="CPF"
                                        variant="outlined"
                                        name="cpf"
                                        onChange={(e) => setSubscriber(maskCPF2(e.currentTarget.value))} />
                                    <Button
                                        onClick={() => handleCertificate()}
                                        variant={"contained"}
                                        sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }}>
                                        <Typography variant='h6'>{genereteCertificate ? 'gerando certificado' : 'buscar certificado'}</Typography>
                                    </Button>
                                </TabPanel>
                            }

                            {currentBase == 'apeprev' &&    
                                <TabPanel value={tabStep} index={8}>
                                    <TitleText>Lista de Inscritos</TitleText>
                                    <TextField fullWidth
                                        sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                        id="CNPJ" label="CNPJ" variant="outlined" name="CNPJ"
                                        value={company}
                                        onChange={(e) => setCompany(maskCNPJ(e.currentTarget.value))}
                                    />
                                    <Button variant={"contained"} sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }} onClick={handleSubscribers}>
                                        <Typography variant='h6'>BUSCAR</Typography>
                                    </Button>
                                </TabPanel>
                            }
                            <TabPanel value={tabStep} index={9}>
                                <TitleText>2ª VIA DO CERTIFICADO</TitleText>
                                <TextField fullWidth
                                    sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                    inputProps={{ maxLength: 14 }}
                                    value={subscriber}
                                    id="cpf"
                                    label="CPF"
                                    variant="outlined"
                                    name="cpf"
                                    onChange={(e) => setSubscriber(maskCPF2(e.currentTarget.value))} />
                                <Button
                                    onClick={() => handleCertificate()}
                                    variant={"contained"}
                                    sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }}>
                                    <Typography variant='h6'>{genereteCertificate ? 'gerando certificado' : 'buscar certificado'}</Typography>
                                </Button>
                            </TabPanel>
                            <TabPanel value={tabStep} index={materialsTabIndex}>
                                {isAgip ? (
                                    <>
                                        <TitleText>Materiais e Apresentações</TitleText>
                                        {listFiles?.length > 0 && (
                                            <TableInfo
                                                title='Arquivos'
                                                variant="secondary"
                                                view={handleViewMaterial}
                                                onView={handleViewMaterial}
                                                onDownload={handleDownloadMaterial}
                                                rows={listFiles}
                                                columns={columnsFiles}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <TitleText>Para acessar a lista de materias valide sua inscrição pelo CPF:</TitleText>
                                        <TextField fullWidth
                                            sx={{ display: "block", m: 5, width: "60%", mx: "auto" }}
                                            inputProps={{ maxLength: 14 }}
                                            value={subscriber}
                                            id="cpf"
                                            label="CPF"
                                            variant="outlined"
                                            name="cpf"
                                            onChange={(e) => setSubscriber(maskCPF2(e.currentTarget.value))} />
                                        <Button
                                            onClick={() => handleMaterials()}
                                            variant={"contained"}
                                            sx={{ display: "block", width: "40%", mx: "auto", borderRadius: 5 }}>
                                            <Typography variant='h6'>{genereteCertificate ? 'carregando lista' : 'carregar lista'}</Typography>
                                        </Button>
                                    </>
                                )}
                            </TabPanel>

                        </Box>
                    </>
            }
            <Dialog open={openGuestDialog} onClose={handleCloseGuestDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Verificar minha hospedagem</DialogTitle>
                <DialogContent>
                    <TextField
                        label="CPF"
                        fullWidth
                        margin="normal"
                        value={cpfGuest}
                        onChange={e => setCpfGuest(maskCPF2(e.target.value))}
                        inputProps={{ maxLength: 14 }}
                        helperText={'Digite seu CPF'}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSearchGuest}
                        disabled={loadingGuest || cpfGuest.replace(/\D/g, '').length !== 11}
                        sx={{ mb: 2 }}
                    >
                        {loadingGuest ? 'Buscando...' : 'Buscar'}
                    </Button>
                    {guestError && <Alert severity="error">{guestError}</Alert>}
                    {guestInfo && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1"><b>Hotel:</b> {guestInfo.hosting?.name || guestInfo.grid?.name}</Typography>
                            <Typography variant="subtitle1"><b>Check-in:</b> {format(parseISO(guestInfo.checkin), 'dd/MM/yy')}</Typography>
                            <Typography variant="subtitle1"><b>Check-out:</b> {format(parseISO(guestInfo.checkout), 'dd/MM/yy')}</Typography>
                            <Typography variant="subtitle1"><b>Diárias:</b> {guestInfo.daily ||
                                differenceInDays(parseISO(guestInfo.checkout), parseISO(guestInfo.checkin))
                            }
                            </Typography>
                            {((guestInfo.hosting?.name || guestInfo.grid?.name).includes('CARIMA')) && <>
                                    <Typography variant="subtitle1"><b>Telefone:</b> (45) 3521-3060 </Typography>
                                    <Typography variant="subtitle1"><b>Endereço:</b> Avenida das Cataratas, 4790, Foz do Iguaçu – PR, Brasil</Typography>
                            </>}
                            {((guestInfo.hosting?.name || guestInfo.grid?.name).includes('CAT PARK')) && <>
                                    <Typography variant="subtitle1"><b>Telefone:</b> (45) 3521-1900 </Typography>
                                    <Typography variant="subtitle1"><b>Endereço:</b> Avenida das Cataratas, 6075, Foz do Iguaçu – PR, Brasil</Typography>
                            </>}
                            {linkMaps.length > 0 && <Button fullWidth variant='contained' color='success' sx={{ mt: 4 }} target="_blank" href={linkMaps}>Mais Informações do Hotel</Button>}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box >
    )
}
